import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Mail, Gift, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import endlessStorageLogo from "@/assets/endless-storage-logo.png";

interface Item {
  name: string;
  quantity: number;
  room: string;
  category?: string;
  notes?: string;
}

export default function BoxAnalyzerResults() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [items, setItems] = useState<Item[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string>("");
  const [userEmail, setUserEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadAnalysisResults();
  }, [sessionId]);

  const loadAnalysisResults = async () => {
    if (!sessionId) {
      toast({
        title: "Error",
        description: "No analysis session found",
        variant: "destructive",
      });
      navigate('/try');
      return;
    }

    try {
      console.log('Loaded results for session', sessionId);
      
      const { data, error } = await supabase
        .from('anonymous_boxes')
        .select('*')
        .eq('session_id', sessionId)
        .single();

      if (error || !data) {
        throw new Error('Analysis not found');
      }

      setItems((data.items as unknown) as Item[] || []);
      setPhotoUrl(data.photo_url || '');
      setUserEmail(data.email || '');
      setIsLoading(false);

    } catch (error: any) {
      console.error('Error loading results:', error);
      toast({
        title: "Error",
        description: "Failed to load analysis results. Please try again.",
        variant: "destructive",
      });
      navigate('/try');
    }
  };

  const handleSaveToAccount = () => {
    // Navigate to signup page with email and session pre-filled
    navigate(`/signup?email=${encodeURIComponent(userEmail)}&claimSession=${sessionId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="h-12 w-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Loading results...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <header className="border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 py-4">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <img src={endlessStorageLogo} alt="Endless Storage" className="h-10" />
          </button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-green-600 mb-4">
              <Sparkles className="h-6 w-6" />
              <h1 className="text-3xl font-bold">✨ We found {items.length} items in your box!</h1>
            </div>
            <p className="text-muted-foreground">Here's everything we detected</p>
          </div>

          {/* Results Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Photo Column */}
            <div>
              <Card className="p-4">
                {photoUrl && (
                  <img 
                    src={photoUrl} 
                    alt="Your box" 
                    className="w-full h-auto rounded-lg"
                  />
                )}
                <p className="text-sm text-muted-foreground text-center mt-2">Your uploaded photo</p>
              </Card>
            </div>

            {/* Items Column */}
            <div>
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">Detected Items</h2>
                  <Badge variant="secondary">{items.length} items</Badge>
                </div>

                {items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="mb-2">We didn't detect items in this photo.</p>
                    <p className="text-sm">You can still save this box and add items manually later.</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {items.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="text-2xl">📦</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{item.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.room} {item.category && `• ${item.category}`}
                          </p>
                        </div>
                        <Badge variant="outline">× {item.quantity}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>

          {/* Save to Account CTA */}
          <Card className="p-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <div className="max-w-xl mx-auto text-center">
              <h2 className="text-2xl font-bold mb-2">Want to save this box?</h2>
              <p className="text-muted-foreground mb-6">
                Results sent to: <strong>{userEmail}</strong>
              </p>

              <div className="space-y-4">
                <Button 
                  onClick={handleSaveToAccount}
                  size="lg" 
                  className="w-full"
                >
                  Save to My Account
                  <Mail className="ml-2 h-5 w-5" />
                </Button>

                <p className="text-xs text-muted-foreground">
                  We'll send you a magic link to access your saved box. No password needed.
                </p>

                <div className="grid gap-2 text-sm pt-4">
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Create unlimited boxes</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Get QR code labels</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Search all your items instantly</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
